import { Logger }    from '../util/logger';
import { Buffer }    from './e.m.c.a.404.types';
import { Listener }  from './e.m.c.a.404.events';

/**
 *
 * @class Node
 *
 * Basic node for the event tree.  The event tree mirrors the structure of the JSON being
 * passed so that the application can specify paths in the tree to indicate which parts
 * of the JSON the application is interested in.
 *
 * Some ideas for the parsing taken from:
 *
 *   - https://github.com/alemures/fast-json
 *   - https://datastation.multiprocess.io/blog/2022-01-06-analyzing-large-json-files-via-partial-json-parsing.html
 *   - https://github.com/uhop/stream-json
 *   - https://github.com/Collaborne/incremental-json-parser
 *   - https://djedr.github.io/posts/jsonhilo-2021-07-29.html
 *   - https://github.com/xtao-org/jsonhilo
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class Node {

  /** name of this node */
  name:      string;

  /** one level down */
  children:  Map<string, Node> = new Map<string, Node>();

  /** one level up */
  parent?:   Node;

  /** the application callbacks for this part of the tree */
  listeners: Listener[];

  /** standard constructor, you have at least provide the parent! */
  constructor(name: string, parent?: Node) {

    this.name      = name;
    this.children  = new Map<string, Node>();
    this.parent    = parent;
    this.listeners = [];
  }

  /** make a new / empty child for self */
  newChild(name: string): Node {

    const node = new Node(name, this);
    this.children.set(name, node);

    return node;
  }

  /** fetch child by name */
  child(name: string): Node | undefined {

    return this.children.get(name);
  }

  /** fetch this child, creating it, if the node doesn't already exist */
  autoChild(name: string): Node {

    const node = this.child(name);
    return node !== undefined ? node : this.newChild(name);
  }

  /** add an application's listener callback to this node */
  addListeners(listeners: Listener[] | Listener) {

    this.listeners = this.listeners.concat(listeners);
  }

  /** check if there are any listeners on this node */
  hasListeners(): boolean {

    return this.listeners.length > 0;
  }

  /** invoke the listeners (if any) */
  listen(data: Buffer, index: string|number|undefined) {

    for (const listener of this.listeners) {

      listener(data, index);
    }
  }

  /** fetch the "absolute" path for this node */
  getPath(): string[] {

    const path = [];

    let node: Node = this;

    while (node.parent !== undefined) {

      path.unshift(node.name);

      node = node.parent;
    }

    return path;
  }

  /** make new (un parented) deep copy of this node */
  clone(): Node {

    const clone = new Node(this.name);

    clone.listeners = this.listeners.slice();

    this.children.forEach((child) => {

      const childClone = child.clone();

      clone.children.set(child.name, childClone);

      childClone.parent = clone;

    });

    return clone;
  }
}
